import type { MessageKey } from "@colorglo/i18n";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@colorglo/ui/components";
import {
  appointmentStatusIcons,
  iconSize,
  MetricRevenue,
  NavCalendar,
  StatusConfirmed,
  StatusPending,
} from "@colorglo/ui/icons";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { type AppointmentView, useAppointments } from "../api/hooks.ts";
import { useFranchise } from "../lib/franchise.ts";
import { useLocale, useT } from "../lib/locale.ts";
import { formatMoney, formatRange, type Temporal, today, zoned } from "../lib/time.ts";

export const Route = createFileRoute("/oversikt")({ component: OverviewPage });

const LIVE_STATUSES = ["pending", "confirmed", "in_progress", "completed"];

/**
 * The franchise overview.
 *
 * Built entirely on `GET /v1/franchises/:id/appointments`, which the calendar already reads —
 * there is no analytics endpoint yet (BUILD.md §1.2 puts the network KPI grid in P10) and
 * inventing one to fill a dashboard would be building the wrong half first. Everything here
 * is arithmetic over the two windows the calendar would have fetched anyway.
 */
function OverviewPage() {
  const t = useT();
  const locale = useLocale();
  const { franchise, franchiseId, timezone } = useFranchise();

  const date = today(timezone);
  const todayIso = date.toString();
  const weekEndIso = date.add({ days: 6 }).toString();

  const todays = useAppointments(franchiseId, todayIso, todayIso);
  const week = useAppointments(franchiseId, todayIso, weekEndIso);

  const live = useMemo(
    () => (todays.data?.data ?? []).filter((row) => LIVE_STATUSES.includes(row.status)),
    [todays.data],
  );

  const sorted = useMemo(
    () => [...live].sort((left, right) => left.start.localeCompare(right.start)),
    [live],
  );

  const pending = useMemo(
    () => (week.data?.data ?? []).filter((row) => row.status === "pending").length,
    [week.data],
  );

  /*
   * CLAUDE.md: SEK and NOK are never summed without a labelled conversion. Every appointment
   * in one franchise is in that franchise's currency, so the reduce below is a same-currency
   * sum by construction — the currency is taken from the rows rather than assumed, and the
   * fallback only ever renders next to a zero.
   */
  const value = useMemo(
    () =>
      live.reduce(
        (sum, row) => ({
          minor: sum.minor + row.total.amount_minor,
          currency: row.total.currency,
        }),
        { minor: 0, currency: "SEK" },
      ),
    [live],
  );

  const upcoming = (week.data?.data ?? []).filter((row) => LIVE_STATUSES.includes(row.status));

  if (todays.isError || week.isError) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("overview.error")}</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-h1">{t("overview.title")}</h1>
          <p className="text-body-sm text-muted-foreground">
            {t("overview.body", { workshop: franchise?.name ?? "" })}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/">
            <NavCalendar {...iconSize.control} aria-hidden="true" />
            {t("overview.open_calendar")}
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="overview.kpi.today"
          hint="overview.kpi.today_hint"
          value={String(live.length)}
          icon={StatusConfirmed}
          loading={todays.isPending}
        />
        <Kpi
          label="overview.kpi.week"
          hint="overview.kpi.week_hint"
          value={String(upcoming.length)}
          icon={NavCalendar}
          loading={week.isPending}
        />
        <Kpi
          label="overview.kpi.pending"
          hint="overview.kpi.pending_hint"
          value={String(pending)}
          icon={StatusPending}
          loading={week.isPending}
        />
        <Kpi
          label="overview.kpi.value"
          hint="overview.kpi.value_hint"
          value={formatMoney(value.minor, value.currency, locale)}
          icon={MetricRevenue}
          loading={todays.isPending}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("overview.today.heading")}</CardTitle>
          <CardDescription>{formatDate(date, locale)}</CardDescription>
        </CardHeader>
        <CardContent>
          {todays.isPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : sorted.length === 0 ? (
            <p className="py-6 text-center text-body-sm text-muted-foreground">
              {t("overview.today.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("overview.column.time")}</TableHead>
                  <TableHead>{t("overview.column.customer")}</TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t("overview.column.service")}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {t("overview.column.resource")}
                  </TableHead>
                  <TableHead>{t("overview.column.status")}</TableHead>
                  <TableHead className="text-right">{t("overview.column.value")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <Row key={row.id} appointment={row} timezone={timezone} locale={locale} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  label,
  hint,
  value,
  icon: Glyph,
  loading,
}: {
  readonly label: MessageKey;
  readonly hint: MessageKey;
  readonly value: string;
  readonly icon: typeof NavCalendar;
  readonly loading: boolean;
}) {
  const t = useT();

  return (
    <Card>
      <CardHeader>
        <CardDescription className="flex items-center gap-2">
          <Glyph {...iconSize.inline} aria-hidden="true" />
          {t(label)}
        </CardDescription>
        <CardTitle className="text-display" data-numeric>
          {loading ? <Skeleton className="h-8 w-16" /> : value}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-caption text-muted-foreground">{t(hint)}</p>
      </CardContent>
    </Card>
  );
}

function Row({
  appointment,
  timezone,
  locale,
}: {
  readonly appointment: AppointmentView;
  readonly timezone: string;
  readonly locale: string;
}) {
  const t = useT();
  const Glyph = appointmentStatusIcons[appointment.status as keyof typeof appointmentStatusIcons];
  const resource = appointment.resources[0]?.name;

  return (
    <TableRow>
      <TableCell>
        <time dateTime={appointment.start}>
          {formatRange(zoned(appointment.start, timezone), zoned(appointment.end, timezone))}
        </time>
      </TableCell>
      <TableCell className="font-medium">{appointment.customer?.name ?? "—"}</TableCell>
      <TableCell className="hidden md:table-cell">
        {appointment.services
          // `name` is a per-locale record, not a string — `String(name)` renders
          // "[object Object]" and TypeScript is perfectly happy with it inside JSX.
          .map((service) => service.name[locale] ?? service.name.sv ?? service.key)
          .join(", ")}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-muted-foreground">
        {resource ?? t("overview.unassigned")}
      </TableCell>
      <TableCell>
        <Badge variant="secondary" className="gap-1">
          {Glyph !== undefined && <Glyph {...iconSize.inline} aria-hidden="true" />}
          {t(`calendar.status.${appointment.status}` as MessageKey)}
        </Badge>
      </TableCell>
      <TableCell className="text-right" data-numeric>
        {formatMoney(appointment.total.amount_minor, appointment.total.currency, locale)}
      </TableCell>
    </TableRow>
  );
}

function formatDate(date: Temporal.PlainDate, locale: string): string {
  return date.toLocaleString(locale, { weekday: "long", day: "numeric", month: "long" });
}
